/**
 * SEOMeta — Dynamic metadata injection component
 *
 * Injects route-specific <title>, <meta description>, canonical link,
 * OpenGraph tags, and Twitter Card tags into the document <head> via
 * react-helmet-async.
 *
 * Spec reference: seo-003 §4 Dynamic Metadata System Design
 *
 * Features:
 * - Environment-based canonical URL generation (strips tracking params)
 * - Route-level fallback defaults via seoDefaults config
 * - SSR-ready: accepts explicit canonicalPath prop for server rendering
 * - No duplicate tags: react-helmet-async deduplicates by attribute key
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { buildCanonicalUrl, getSiteBaseUrl } from "utils/seoCanonical";
import { resolveRouteMeta } from "config/seoDefaults";

export interface SEOMetaProps {
  /** Page-specific title. Appended with " | Moderate Politics" suffix. */
  title?: string;
  /** Page-specific meta description (max ~160 chars recommended). */
  description?: string;
  /** Absolute URL for og:image / twitter:image. Falls back to default OG image. */
  ogImage?: string;
  /** OpenGraph object type. Defaults to 'website'. */
  ogType?: "website" | "article" | "profile";
  /**
   * Explicit canonical path override (e.g. "/conversations-nearby/123").
   * When omitted, canonical is derived from the current router location,
   * stripping tracking query parameters.
   * Use this prop when rendering server-side or when the canonical path
   * differs from the active URL.
   */
  canonicalPath?: string;
  /** When true, injects "noindex, nofollow" robots directive. */
  noIndex?: boolean;
  /** JSON-LD structured data string to inject. */
  schema?: string;
}

const SITE_NAME = "Moderate Politics";
const DEFAULT_TITLE = `${SITE_NAME} | Constructive Political Discourse`;
const DEFAULT_DESC =
  "Engage in constructive political discourse. Join room-based debates, read news feeds, and connect with people nearby.";
const DEFAULT_IMAGE = `${getSiteBaseUrl()}/assets/og-default.png`;

export const SEOMeta: React.FC<SEOMetaProps> = ({
  title,
  description,
  ogImage,
  ogType,
  canonicalPath,
  noIndex,
  schema,
}) => {
  const { pathname, search } = useLocation();

  // Resolve route-level defaults for the current path
  const routeDefaults = resolveRouteMeta(canonicalPath || pathname);

  // Build display values: prop > route default > global fallback
  const resolvedTitle = title ?? routeDefaults.title;
  const displayTitle = resolvedTitle
    ? `${resolvedTitle} | ${SITE_NAME}`
    : DEFAULT_TITLE;
  const displayDesc = description ?? routeDefaults.description ?? DEFAULT_DESC;
  const displayImage = ogImage ?? routeDefaults.ogImage ?? DEFAULT_IMAGE;
  const displayOgType = ogType ?? routeDefaults.ogType ?? "website";
  const displayNoIndex = noIndex ?? routeDefaults.noIndex ?? false;

  // Build canonical URL — explicit override takes priority, then strip tracking params
  const canonicalUrl = canonicalPath
    ? buildCanonicalUrl(canonicalPath)
    : buildCanonicalUrl(pathname, search);

  return (
    <Helmet>
      {/* ── Core SEO ─────────────────────────────────────────────── */}
      <title>{displayTitle}</title>
      <meta name="description" content={displayDesc} />
      <link rel="canonical" href={canonicalUrl} />

      {/* ── Robots directive ─────────────────────────────────────── */}
      <meta
        name="robots"
        content={displayNoIndex ? "noindex, nofollow" : "index, follow"}
      />

      {/* ── OpenGraph / Facebook ─────────────────────────────────── */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={displayTitle} />
      <meta property="og:description" content={displayDesc} />
      <meta property="og:type" content={displayOgType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={displayImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* ── Twitter Cards ────────────────────────────────────────── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ModeratePolitics" />
      <meta name="twitter:title" content={displayTitle} />
      <meta name="twitter:description" content={displayDesc} />
      <meta name="twitter:image" content={displayImage} />

      {/* ── Structured Data / JSON-LD ────────────────────────────── */}
      {schema && <script type="application/ld+json">{schema}</script>}
    </Helmet>
  );
};

export default SEOMeta;
